import {
  formatReportDate,
  ReportDate,
  type ReportDateFormat,
} from "@/components/ui/report/ReportDate/ReportDate";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import {
  type ReportSpacing,
  type ReportTextStyle,
  resolveReportSpacingStyle,
  resolveReportTextStyle,
} from "@/components/ui/report/reportPresentation";
import { cn } from "@/lib/utils";

/** Shown when no briefing title is provided. */
export const REPORT_HEADER_BRIEFING_TITLE_PLACEHOLDER = "Briefing title";

export interface ReportHeaderProps {
  briefingTitle?: string | null;
  dateValue?: string | null;
  dateFormat?: ReportDateFormat | null;
  /** Title typography; textAlign also aligns the date line. */
  titleStyle?: ReportTextStyle | null;
  spacing?: ReportSpacing | null;
  className?: string;
}

/** Report page header: briefing title and optional date. */
export const ReportHeader = ({
  briefingTitle,
  dateValue,
  dateFormat,
  titleStyle,
  spacing,
  className,
}: ReportHeaderProps) => {
  const theme = useReportTheme();
  const displayTitle =
    briefingTitle != null && briefingTitle.trim() !== ""
      ? briefingTitle.trim()
      : REPORT_HEADER_BRIEFING_TITLE_PLACEHOLDER;

  const customFontFamily = theme?.fontFamily;
  const titleTextAlign = titleStyle?.textAlign ?? "left";

  const resolvedDateFormat = dateFormat ?? "monthDayYear";
  const hasDate =
    dateValue != null &&
    dateValue !== "" &&
    formatReportDate(dateValue, resolvedDateFormat) != null;

  return (
    <header
      className={cn("flex w-full flex-col gap-1 px-4", className)}
      style={resolveReportSpacingStyle(spacing)}
    >
      <p
        className={cn(
          "m-0 wrap-break-word text-xl font-normal leading-7 text-foreground",
          !customFontFamily && "font-serif",
        )}
        style={{
          ...(customFontFamily && { fontFamily: customFontFamily }),
          ...resolveReportTextStyle(titleStyle),
        }}
      >
        {displayTitle}
      </p>
      {hasDate && dateValue != null ? (
        <div
          className={cn(
            "flex w-full",
            titleTextAlign === "center"
              ? "justify-center"
              : titleTextAlign === "right"
                ? "justify-end"
                : "justify-start",
          )}
        >
          <ReportDate
            dateValue={dateValue}
            dateFormat={resolvedDateFormat}
            align={titleTextAlign === "right" ? "right" : "left"}
          />
        </div>
      ) : null}
    </header>
  );
};
